import styles from './Tile.scss';

import cx from 'classnames';
import { ReferencedEntry } from 'contentful';
import { TFunction } from 'i18next';
import React from 'react';

import { useTranslation } from 'react-i18next';
import { ContentType, Subject } from 'src/api/contentful/contentTypes.cloverdotcom';
import { ContentLink } from 'src/components/ContentLink/ContentLink';
import { Background as ContentfulBackground } from 'src/components/Contentful/Background';
import { Text } from 'src/components/Contentful/Text';
import { ProductMonthlyPrice } from 'src/components/ProductPrice/ProductMonthlyPrice';
import { ProductPrice } from 'src/components/ProductPrice/ProductPrice';
import { withBrowser } from 'src/components/Responsive/WithBrowser';
import { useMobile } from 'src/hooks/UseResponsive';
import { getContentType } from 'src/utils/CloverdotcomEntryUtil';
import { getField, isEntry } from 'src/utils/EntryUtil';
import { Environment } from 'src/utils/Environment';
import { shouldRedirectExistingMerchant } from 'src/utils/ExistingMerchant';
import { testAttribute } from 'src/utils/Serenity';

export const getTileText = (t: TFunction, defaultCta: string, entry?: ReferencedEntry<Subject>) => {
  if (!Environment.current.isExistingMerchant) return defaultCta;

  const link = getField(entry, 'link');

  switch (getContentType(link)) {
    case(ContentType.EXTERNAL_LINK):
      if (shouldRedirectExistingMerchant(link)) {
        return t('contact-sales');
      }
    default:
      return t('get-started');
  }
};

export interface ITile extends React.AllHTMLAttributes<HTMLElement> {
  entry?: ReferencedEntry<Subject>;
  single?: boolean;
  mobile?: boolean;
}

const Tile: React.FC<ITile> = ({ entry, mobile, single, ...props }) => {
  const { t } = useTranslation();
  const isMobile = useMobile();

  if (!isEntry(entry)) return null;

  const { image, mobileImage, link, internalName } = entry.fields;
  const ctaMessage = getField(link, 'title');
  const productImage = isMobile && mobileImage || image;

  return (
    <ContentLink
      className={cx(styles.tile, { [styles.tileSingle]: single })}
      entry={link}
      hideTitle
      alwaysRenderChildren
    >
      <ContentfulBackground
        autoHeight={!!mobile || !single}
        autoWidth={!mobile && !!single}
        className={styles.tileBackground}
        asset={productImage}
        {...props}
      >
        <div className={styles.tileContent}>
          <Text tagName="h2" entry={entry} field="title" className={styles.tagLine} />
          {ctaMessage && (
            <div className={styles.tileControls}>
              <button
                data-test={`shop-now-${testAttribute(ctaMessage)}`}
                className={styles.tileBtn}
              >
                {getTileText(t, ctaMessage, entry)}
              </button>
              <div className={styles.pricing}>
                <ProductPrice
                  className={styles.price}
                  entry={entry}
                  {
                  ...{
                    'data-test': testAttribute(internalName ?
                      internalName.replace('-', ' ') :
                      `shop-now-${testAttribute(ctaMessage)}-price`),
                  }
                  }
                />
                  <ProductMonthlyPrice className={styles.installmentPrice} entry={entry} />
              </div>
            </div>
          )}
        </div>
      </ContentfulBackground>
    </ContentLink>
  );
};

const Browsered = withBrowser(browser => ({ mobile: browser.is.mobile }))(Tile);
type Browsered = typeof Browsered;

export { Browsered as Tile, Tile as _Tile };
